import React from "react";
import { Skeleton, Space, Tag, Tooltip, Typography } from "antd";
import { ExportOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

const { Text } = Typography;

/**
 * Who the person in this chat is, in CRM terms: a Lead, an Account, both, or
 * somebody we have never seen.
 *
 * "Both" is two badges rather than a third kind of badge — a number genuinely
 * can belong to a lead being worked and to an existing customer, and the rep
 * needs to reach either record.
 */

const RecordLink = ({ to, children, color, tooltip }) => {
  const tag = (
    <Tag color={color} style={{ marginInlineEnd: 4, cursor: "pointer" }}>
      {children}
      {/* Opens in a new tab on purpose: navigating away mid-conversation and
          losing the thread is the worst thing this panel could do. */}
      <Link
        to={to}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "inherit", marginLeft: 6 }}
        onClick={(event) => event.stopPropagation()}
      >
        <ExportOutlined style={{ fontSize: 11 }} />
      </Link>
    </Tag>
  );
  return tooltip ? <Tooltip title={tooltip}>{tag}</Tooltip> : tag;
};

const IdentityBadges = ({ identity, loading, showEmpty = true }) => {
  // Never fall through to "Unknown" while the lookup is still running — that
  // reads as a definite answer and it is the likeliest bug in this panel.
  if (loading) {
    return <Skeleton.Button size="small" active style={{ width: 90, height: 22 }} />;
  }
  if (!identity) return null;

  const leads = identity.leads || [];
  const accounts = identity.accounts || [];
  const contacts = identity.contacts || [];

  if (!leads.length && !accounts.length && !contacts.length) {
    return showEmpty ? <Tag>Unknown</Tag> : null;
  }

  return (
    <Space size={0} wrap>
      {leads.map((lead) => (
        <RecordLink
          key={lead.id}
          to={lead.url_path}
          color="green"
          tooltip={
            lead.match_type === "suffix"
              ? "Matched on the last 10 digits only — check this is the right person"
              : lead.lead_number || undefined
          }
        >
          Lead · {lead.display_name}
        </RecordLink>
      ))}

      {accounts.map((account) => (
        <RecordLink
          key={account.id}
          to={account.url_path}
          color="blue"
          tooltip={
            account.matched_contact
              ? `via contact ${account.matched_contact.display_name}`
              : account.account_number || undefined
          }
        >
          Account · {account.display_name}
          {account.matched_contact ? (
            <Text style={{ fontSize: 11, opacity: 0.75, marginLeft: 4 }}>
              via {account.matched_contact.display_name}
            </Text>
          ) : null}
        </RecordLink>
      ))}

      {/* A contact with no parent account has nowhere to link to, so it is
          shown but not made clickable. */}
      {contacts
        .filter((contact) => !contact.account_id)
        .map((contact) => (
          <Tooltip key={contact.id} title="No parent account">
            <Tag>Contact · {contact.display_name}</Tag>
          </Tooltip>
        ))}
    </Space>
  );
};

export default IdentityBadges;
